/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// Polyfill Buffer for browser (required by bip39 library)
import {Buffer} from 'buffer';
import React from 'react';
import {DeviceProvider} from './src/web-core/misc/useDevice';
import {LogoutConfirmationProvider} from './src/contexts/LogoutConfirmationContext';
import {IbmNotificationToaster} from './src/components/IbmNotificationToaster';
import {startDevWasmReloadWatcher} from './src/wasm/devWasmReload';
import {lockMobileZoom} from './src/misc/lockMobileZoom';
import {captureAttribution} from './src/components/ava/attribution';
import {capturePaperKeyFromUrl, stashPaperKeyFromUrl} from './src/auth/paperKeyHandoff';

(window as any).Buffer = Buffer;

export const onClientEntry = () => {
    // FIRST, before anything else can read the URL: a Recovery Kit QR hands
    // /authorize the paper key in the fragment (or, for kits printed before
    // 2026-09-21, the query). Take it into memory now. It is NOT scrubbed
    // from the address bar here — Gatsby has not loaded the page yet, and a
    // URL change before that breaks its resource lookup (paperKeyHandoff.ts
    // explains). onRouteUpdate below scrubs it the moment the route renders.
    stashPaperKeyFromUrl();
    // App-like mobile: block iOS-Safari page pinch-zoom (the viewport meta in
    // html.tsx covers Android/Chrome + standalone PWA; iOS Safari ignores it).
    lockMobileZoom();
    // Dev only: auto-reload the page when the device WASM is republished under an
    // open tab. No-op in prod — enforced via NODE_ENV in
    // shouldStartDevWasmReloadWatcher, NOT the version pin (ships as 'latest').
    startDevWasmReloadWatcher();
};

// Acquisition attribution (src/components/ava/attribution.ts): on every page
// load and client-side navigation, keep the URL's utm_* tags for the tab and
// scrub them from the address bar. Ava stamps the stash on the search it
// mints, so a tagged landing anywhere on the site attributes the search that
// follows. Runs after the route renders, so it never affects hydration.
export const onRouteUpdate = ({ location }: { location: { search?: string } }) => {
    // The route has rendered, so the URL may change now. The paper key first:
    // scrub what onClientEntry stashed on a page load, and stash-and-scrub
    // what a client-side navigation into /authorize#paper_key=… brings (that
    // never passes through onClientEntry). Then attribution.
    capturePaperKeyFromUrl();
    captureAttribution(location);
};

// Wrap the root element with providers.
// LogoutConfirmationProvider must be inside DeviceProvider since it uses useDeviceContext.
// Device setup is handled lazily by DeviceProvider's setup prompter:
// the first device.* op that finds no WasmDeviceRecord triggers the
// setup ceremony as a sibling overlay; routes don't need to preflight.
export const wrapRootElement = ({ element }: { element: React.ReactNode }) => {
    return React.createElement(
        DeviceProvider,
        null,
        React.createElement(
            LogoutConfirmationProvider,
            null,
            React.createElement(IbmNotificationToaster, null),
            element
        )
    );
};
